(() => {
  const serviceUrl = (root) => String(root?.dataset.supportServiceUrl || '').trim().replace(/\/+$/, '');
  const showStatus = (node, message, error = false) => { if (!node) return; node.textContent = message; node.classList.toggle('is-error', error); };
  const OFFLINE_MESSAGE = 'We could not connect to support right now. Please try again in a moment.';
  const isDropped = (error) => error?.name === 'AbortError' || error?.name === 'TypeError' || /failed to fetch/i.test(String(error?.message || ''));
  // A dropped connection is common on a long international route, and it usually clears within
  // seconds, so a submission is sent again before the customer is told anything went wrong. The
  // service recognises the repeat by its request id, so a retry can never open a second ticket.
  const readJson = async (url, options, { retries = 0, retryDelayMs = 1500, timeoutMs = 25000 } = {}) => {
    let response;
    for (let attempt = 0; ; attempt += 1) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : 0;
      try {
        response = await fetch(url, controller ? { ...options, signal: controller.signal } : options);
        break;
      } catch (error) {
        if (!isDropped(error)) throw error;
        if (attempt >= retries) throw new Error(OFFLINE_MESSAGE);
        await new Promise((resolve) => window.setTimeout(resolve, retryDelayMs * (attempt + 1)));
      } finally {
        window.clearTimeout(timer);
      }
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Support email temporarily unavailable.');
    return data;
  };
  const MAX_IMAGE_EDGE = 2048;
  const COMPRESS_OVER_BYTES = 512 * 1024;
  const fitWithin = (width, height, max) => { const scale = Math.min(1, max / Math.max(width, height)); return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }; };
  // Phone photos arrive far larger than a support ticket needs, and the store's file library
  // caps one image at 20 MiB. Downscale and re-encode before upload; a PNG becomes WebP so a
  // multi-megabyte screenshot shrinks without losing its transparency. Any failure (old browser,
  // oversized decode, canvas refused) returns the untouched file so nothing is ever lost.
  const compressImage = async (file) => {
    if (!/^image\//.test(file.type) || file.size <= COMPRESS_OVER_BYTES) return file;
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const size = fitWithin(bitmap.width, bitmap.height, MAX_IMAGE_EDGE);
      const canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, size.width, size.height);
      bitmap.close?.();
      const type = file.type === 'image/png' ? 'image/webp' : 'image/jpeg';
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.85));
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], file.name.replace(/\.[^.]+$/, type === 'image/webp' ? '.webp' : '.jpg'), { type: blob.type });
    } catch { return file; }
  };
  const MAX_FILE_BYTES = 19 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
  const MAX_IMAGES = 9;
  const MAX_VIDEOS = 1;
  const isVideoFile = (file) => /^video\//.test(String(file?.type || ''));
  const VALIDATION_COPY = {
    en: { valueMissing: 'Please fill in this field.', typeMismatch: 'Please enter a valid email address.' },
    de: { valueMissing: 'Bitte füllen Sie dieses Feld aus.', typeMismatch: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' },
    fr: { valueMissing: 'Veuillez renseigner ce champ.', typeMismatch: 'Veuillez saisir une adresse e-mail valide.' },
    it: { valueMissing: 'Compila questo campo.', typeMismatch: 'Inserisci un indirizzo email valido.' },
  };
  // The bubble the browser draws is written in the browser's own language, so a Chinese browser
  // tells an English shopper 请填写此字段。Replace it with the storefront language, which
  // <html lang> carries and the language switcher changes, and fall back to English.
  const localizeValidation = (form) => {
    const copy = () => VALIDATION_COPY[String(document.documentElement.lang || 'en').toLowerCase().split('-')[0]] || VALIDATION_COPY.en;
    form.addEventListener('invalid', (event) => {
      const field = event.target;
      if (!field || typeof field.setCustomValidity !== 'function') return;
      const text = copy();
      field.setCustomValidity(field.validity.valueMissing ? text.valueMissing : field.validity.typeMismatch ? text.typeMismatch : '');
    }, true);
    form.addEventListener('input', (event) => { event.target?.setCustomValidity?.(''); }, true);
  };
  const fileKey = (file) => `${file.name}:${file.size}:${file.lastModified}`;
  // The picker hands over the whole selection every time, so a new pick is merged into what
  // is already staged instead of replacing it: photos stack up to 9, video stays at 1.
  const stagedFiles = (form) => {
    if (!form.__monsgaFiles) form.__monsgaFiles = [];
    return form.__monsgaFiles;
  };
  const clearStagedFiles = (form) => { stagedFiles(form).length = 0; };
  const addStagedFiles = (form, incoming) => {
    const files = stagedFiles(form);
    let error = '';
    for (const file of incoming) {
      const videos = files.filter(isVideoFile).length;
      const images = files.length - videos;
      const total = files.reduce((sum, item) => sum + item.size, 0) + file.size;
      if (files.some((item) => fileKey(item) === fileKey(file))) continue;
      if (!/^(image|video)\//.test(String(file.type || ''))) error = 'Only photos and videos can be attached.';
      else if (file.size > MAX_FILE_BYTES) error = 'Please keep each file under 19 MB.';
      else if (isVideoFile(file) && videos >= MAX_VIDEOS) error = 'Only one video can be attached.';
      else if (!isVideoFile(file) && images >= MAX_IMAGES) error = 'Up to 9 photos can be attached.';
      else if (total > MAX_TOTAL_BYTES) error = 'Please keep all attachments under 20 MB in total.';
      else files.push(file);
    }
    return error;
  };
  const renderStagedFiles = (form) => {
    const files = stagedFiles(form);
    const label = form.querySelector('[data-support-file-name]');
    const list = form.querySelector('[data-support-file-list]');
    if (label) label.hidden = files.length > 0;
    if (!list) return;
    list.textContent = '';
    files.forEach((file, index) => {
      const item = document.createElement('li');
      item.className = 'monsga-support-form__file-item';
      const name = document.createElement('span');
      name.textContent = file.name;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'monsga-support-form__file-remove';
      remove.dataset.supportRemoveFile = String(index);
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.textContent = '\u00d7';
      item.append(name, remove);
      list.append(item);
    });
    list.hidden = files.length === 0;
  };
  // The picker has already resized every photo, so this only reads the list: a file that still
  // breaks a cap (or cannot be read) aborts the whole submission instead of being dropped.
  const readStagedFiles = async (form) => {
    const files = stagedFiles(form);
    if (files.reduce((sum, item) => sum + item.size, 0) > MAX_TOTAL_BYTES) throw new Error('Please keep all attachments under 20 MB in total.');
    const attachments = [];
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) throw new Error('Please keep each file under 19 MB.');
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(String(reader.result || '')));
        reader.addEventListener('error', () => reject(new Error('The attachment could not be read.')));
        reader.readAsDataURL(file);
      });
      attachments.push({ name: file.name, type: file.type || 'application/octet-stream', dataUrl });
    }
    return attachments;
  };
  // Sent with every submission so the service recognises a retry of the same request.
  const newRequestId = () => {
    try { return crypto.randomUUID(); } catch { return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; }
  };
  const submitTicket = async (root, form, attachments) => {
    const base = serviceUrl(root);
    if (!base) throw new Error('Support email service is not configured.');
    const fields = {};
    const data = new FormData(form);
    data.forEach((value, key) => { const match = key.match(/^contact\[([^\]]+)\]$/); if (match && typeof value === 'string') fields[match[1]] = value; });
    return readJson(`${base}/api/tickets`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ fields, attachments, requestId: newRequestId() }) }, { retries: 2 });
  };
  const syncMode = (form) => {
    const yes = form.querySelector('[data-support-purchased-option]')?.value;
    const purchased = form.querySelector('[data-support-purchased-option]:checked')?.value === yes;
    const purchasedMode = form.querySelector('[data-support-mode="purchased"]');
    const notPurchasedMode = form.querySelector('[data-support-mode="not-purchased"]');
    if (purchasedMode) purchasedMode.hidden = !purchased;
    if (notPurchasedMode) notPurchasedMode.hidden = purchased;
  };
  const resetForm = (form, root) => {
    if (!form) return;
    form.reset();
    clearStagedFiles(form);
    renderStagedFiles(form);
    syncMode(form);
    form.querySelectorAll('[data-support-fallback], [data-support-failure]').forEach((node) => { node.hidden = true; });
    root?.querySelectorAll('[data-support-form-status], [data-support-page-status], [data-support-status]').forEach((node) => showStatus(node, ''));
  };
  const wireForm = (form, root, done, undo = () => {}) => {
    if (!form || !root || form.dataset.supportReady === 'true') return;
    form.dataset.supportReady = 'true';
    const status = form.querySelector('[data-support-form-status]') || root.querySelector('[data-support-status]');
    const fallback = form.querySelector('[data-support-fallback]');
    const failure = form.querySelector('[data-support-failure]');
    const failureMessage = form.querySelector('[data-support-failure-message]');
    const attachment = form.querySelector('[data-support-attachment]');
    form.querySelectorAll('[data-support-purchased-option]').forEach((option) => option.addEventListener('change', () => syncMode(form)));
    localizeValidation(form);
    attachment?.addEventListener('change', () => {
      const chosen = Array.from(attachment.files || []);
      attachment.value = '';
      // Photos are resized and re-encoded here rather than at submit time, so the size limits
      // below measure what will actually be uploaded: a 5 MB phone photo counts as a few hundred KB.
      if (chosen.length) showStatus(status, 'Preparing attachments...');
      form.__monsgaStaging = (async () => {
        const prepared = [];
        for (const file of chosen) prepared.push(await compressImage(file));
        return prepared;
      })();
      form.__monsgaStaging.then((prepared) => {
        const error = addStagedFiles(form, prepared);
        renderStagedFiles(form);
        showStatus(status, error, Boolean(error));
        if (error && failureMessage) failureMessage.textContent = error;
        if (failure) failure.hidden = !error;
      });
    });
    form.querySelector('[data-support-file-list]')?.addEventListener('click', (event) => {
      const index = Number(event.target.closest('[data-support-remove-file]')?.dataset.supportRemoveFile);
      if (!Number.isInteger(index)) return;
      stagedFiles(form).splice(index, 1);
      renderStagedFiles(form);
      showStatus(status, '');
      if (failure) failure.hidden = true;
    });
    form.querySelector('[data-support-retry]')?.addEventListener('click', (event) => {
      event.preventDefault();
      resetForm(form, root);
      form.querySelector('input, select, textarea, button')?.focus();
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const body = String(form.querySelector('[name="contact[body]"]')?.value || '').trim();
      if (body.length < 3) { showStatus(status, 'Please enter at least 3 characters.', true); return; }
      const button = form.querySelector('[type="submit"]');
      if (button) button.disabled = true;
      if (fallback) fallback.hidden = true;
      if (failure) failure.hidden = true;
      const failStep = (error) => {
        showStatus(status, '');
        if (fallback) fallback.hidden = false;
        if (failureMessage) failureMessage.textContent = error.message;
        if (failure) { failure.hidden = false; failure.focus(); }
        if (button) button.disabled = false;
      };
      let attachments;
      try {
        if (!serviceUrl(root)) throw new Error('Support email service is not configured.');
        if (form.__monsgaStaging) await form.__monsgaStaging;
        attachments = await readStagedFiles(form);
      } catch (error) {
        failStep(error);
        return;
      }
      // The customer should not sit through a 15 MB video travelling to the store and back, so
      // the thank-you panel shows at once and the send carries on in the background. Should the
      // send fail anyway we put the form back (every answer is untouched) and say what happened.
      done();
      submitTicket(root, form, attachments).then(() => { if (button) button.disabled = false; }).catch((error) => { undo(); failStep(error); });
    });
  };
  const initWidget = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    const form = root.querySelector('.monsga-support-form');
    const formRoot = form?.closest('[data-support-form]') || root;
    const dialog = root.querySelector('.monsga-support-widget__dialog');
    const backdrop = root.querySelector('.monsga-support-widget__backdrop');
    const button = root.querySelector('[data-support-open]');
    const stage = root.querySelector('[data-support-form-stage]');
    const submitted = root.querySelector('[data-support-submitted]');
    const status = root.querySelector('[data-support-status]');
    const started = root.querySelector('[data-support-started]');
    let hideTimer;
    // An empty locale list follows the *browser*, which is why an Italian page showed 10月5日 on a
    // Chinese browser. The label above comes from the page's language, so the date should too.
    if (started) started.textContent = new Date().toLocaleString(document.documentElement.lang || [], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    const syncPageChrome = (open) => document.body.classList.toggle('monsga-support-open', open);
    const hide = () => { syncPageChrome(false); dialog?.classList.remove('is-visible'); backdrop?.classList.remove('is-visible'); button?.setAttribute('aria-expanded', 'false'); window.clearTimeout(hideTimer); hideTimer = window.setTimeout(() => { if (dialog) dialog.hidden = true; if (backdrop) backdrop.hidden = true; }, 240); };
    const show = () => { syncPageChrome(true); window.clearTimeout(hideTimer); if (dialog) dialog.hidden = false; if (backdrop) { backdrop.hidden = false; backdrop.classList.add('is-visible'); } button?.setAttribute('aria-expanded', 'true'); window.requestAnimationFrame(() => dialog?.classList.add('is-visible')); };
    button?.addEventListener('click', show);
    backdrop?.addEventListener('click', hide);
    root.querySelector('.monsga-support-widget__header [data-support-close]')?.addEventListener('click', hide);
    submitted?.querySelector('[data-support-new-form]')?.addEventListener('click', (event) => {
      event.preventDefault();
      resetForm(form, root);
      if (stage) stage.hidden = false;
      if (submitted) submitted.hidden = true;
      form?.querySelector('input, select, textarea, button')?.focus();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && dialog && !dialog.hidden) hide(); });
    wireForm(form, formRoot, () => { if (stage) stage.hidden = true; if (submitted) { submitted.hidden = false; submitted.focus(); } }, () => { if (stage) stage.hidden = false; if (submitted) submitted.hidden = true; });
  };
  const initPageForm = (form) => {
    const root = form.closest('[data-support-form="page"]');
    const submitted = root?.querySelector('[data-support-submitted]');
    submitted?.querySelector('[data-support-new-form]')?.addEventListener('click', (event) => {
      event.preventDefault();
      resetForm(form, root);
      form.hidden = false;
      if (submitted) submitted.hidden = true;
      form.querySelector('input, select, textarea, button')?.focus();
    });
    wireForm(form, root, () => { form.hidden = true; if (submitted) { submitted.hidden = false; submitted.focus(); } }, () => { form.hidden = false; if (submitted) submitted.hidden = true; });
  };
  const initAll = () => { document.querySelectorAll('[data-support-widget]').forEach(initWidget); document.querySelectorAll('[data-support-form="page"] .monsga-support-form').forEach(initPageForm); };
  initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);
})();
